import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { messages, defaultLocale, locales, type Locale } from "@/i18n/settings";
import { DOMAIN, ENVIRONMENT } from "@/app/lib/enums";
import { ChatHistory } from "@/types";

export function cn(...inputs: ClassValue[]) {
   return twMerge(clsx(inputs));
}

export const mapUrlToDomain = (url: string): string => {
   const urlMappings = {
      "http://149.28.148.15:4100": "https://stagingapi.credible.social",
      "http://149.28.148.15:4000": "https://productionapi.credible.social",
      "http://45.76.184.211:4100": "https://stagingapi.credible.social",
      "http://45.76.184.211:4000": "https://productionapi.credible.social"
   };

   for (const [ipUrl, domainUrl] of Object.entries(urlMappings)) {
      if (url.startsWith(ipUrl)) {
         return url.replace(ipUrl, domainUrl);
      }
   }

   return url;
};

export const getEnvironment = (): ENVIRONMENT => {
   return process.env.NODE_ENV === ENVIRONMENT.DEVELOPMENT
      ? ENVIRONMENT.DEVELOPMENT
      : process.env.NEXT_PUBLIC_BETA_VERSION
        ? ENVIRONMENT.STAGING
        : ENVIRONMENT.PRODUCTION;
};
export const getReferer = () => {
   const environment = getEnvironment();
   switch (environment) {
      case ENVIRONMENT.DEVELOPMENT:
         return `http://${DOMAIN.LOCAL_FRONTEND}`;
      case ENVIRONMENT.STAGING:
         return `https://${DOMAIN.CRYPTOCURRENCY_MARKET_STAGING_FRONTEND}`;
      case ENVIRONMENT.PRODUCTION:
         return `https://${DOMAIN.CRYPTOCURRENCY_MARKET_FRONTEND}`;
   }
};

export function getLocalizedMessageFromRequest(
   req: {
      headers: Headers;
      cookies?: { get: (name: string) => { value: string } | undefined };
   },
   messageKey: string,
   fallback?: string
): string {
   try {
      const headers = req.headers;
      const acceptLanguage = headers.get("accept-language") || "";
      const cookieLocale = req.cookies?.get("NEXT_LOCALE")?.value as Locale | undefined;

      let locale: Locale = defaultLocale;
      const isCookieLocale =
         (cookieLocale && locales.includes(cookieLocale)) ||
         acceptLanguage.includes("en") ||
         acceptLanguage.includes("vi");

      if (isCookieLocale) {
         locale = cookieLocale as Locale;
      }

      const localeMessages = messages[locale];
      const keys = messageKey.split(".");
      let message: any = localeMessages;

      for (const key of keys) {
         if (message && typeof message === "object" && key in message) {
            message = message[key];
         } else {
            message = null;
            break;
         }
      }

      return typeof message === "string" ? message : fallback || messageKey;
   } catch (error) {
      console.error("Error getting localized message:", error);
      return fallback || messageKey;
   }
}
export const sanitizeCollectionName = (value: string): string => {
   return value.replace(/[^a-zA-Z0-9\s]/g, "").replace(/\s+/g, "_");
};

// ============================================================
// Language Utilities
// ============================================================

export type LanguageCode = "vi" | "en";

/**
 * Map from detected language (full name from LLM) → ISO language code.
 * Extend this map when adding new languages.
 */
const DETECTED_LANGUAGE_TO_CODE: Record<string, LanguageCode> = {
   vietnamese: "vi",
   vietnam: "vi",
   "tiếng việt": "vi",
   english: "en",
   "tiếng anh": "en"
};

const DEFAULT_LANGUAGE_CODE: LanguageCode = "vi";

export const toLanguageCode = (detectedLanguage: string): LanguageCode => {
   const normalized = detectedLanguage.toLowerCase().trim();
   for (const [key, code] of Object.entries(DETECTED_LANGUAGE_TO_CODE)) {
      if (normalized.includes(key)) return code;
   }
   return DEFAULT_LANGUAGE_CODE;
};

export function formatDuration(ms: number): string {
   if (ms < 1000) return `${ms}ms`;
   const seconds = Math.floor(ms / 1000);
   if (seconds < 60) return `${seconds}s`;
   const minutes = Math.floor(seconds / 60);
   const remainingSeconds = seconds % 60;
   return `${minutes}m ${remainingSeconds}s`;
}
/** Mirrors the backend sort so a locally merged page keeps the server's order. */
export function compareChatHistoryOrder(a: ChatHistory, b: ChatHistory): number {
   if (Boolean(a.pinnedAt) !== Boolean(b.pinnedAt)) {
      return a.pinnedAt ? -1 : 1;
   }
   if (a.pinnedAt && b.pinnedAt) {
      const byPin = new Date(b.pinnedAt).getTime() - new Date(a.pinnedAt).getTime();
      if (byPin !== 0) return byPin;
   }
   const byRecency =
      new Date(b.lastMessageAt || b.createdAt).getTime() -
      new Date(a.lastMessageAt || a.createdAt).getTime();
   if (byRecency !== 0) return byRecency;
   // ids are 24-char hex ObjectIds, so string compare matches the backend's `_id: -1` tiebreaker.
   return b.id.localeCompare(a.id);
}

export function mergeChatHistories(
   current: ChatHistory[],
   incoming: ChatHistory[]
): ChatHistory[] {
   const byId = new Map(current.map((history) => [history.id, history]));
   incoming.forEach((history) => byId.set(history.id, history));
   return [...byId.values()].sort(compareChatHistoryOrder);
}
